import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { TrendingUp, CheckCircle, Clock, AlertCircle, Calendar, RefreshCw, ArrowLeft } from 'lucide-react';
import { useAllProtocolsCompletionStats } from '../../hooks/useProtocols';
import './ProtocolAnalyticsDashboard.css';

const ProtocolAnalyticsDashboard = ({ onBack }) => {
  const { t } = useTranslation('protocols');
  const [dateRange, setDateRange] = useState({
    start: '',
    end: ''
  });
  
  const { stats, loading, error, refresh } = useAllProtocolsCompletionStats(
    dateRange.start && dateRange.end ? dateRange : null
  );

  const formatDuration = (seconds) => {
    if (!seconds || seconds === 0) return 'N/A';
    const hours = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    if (hours > 0) return `${hours}h ${mins}m`;
    return `${mins}m`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString();
  };

  const getCompletionRateColor = (rate) => {
    if (rate >= 90) return '#10b981'; // green
    if (rate >= 70) return '#f59e0b'; // yellow
    return '#ef4444'; // red
  };

  const getComplianceScoreColor = (score) => {
    if (score >= 90) return '#10b981'; // green
    if (score >= 70) return '#f59e0b'; // yellow
    return '#ef4444'; // red
  };

  // Calculate overall statistics
  const overallStats = stats.length > 0 ? {
    totalProtocols: stats.length,
    totalExecutions: stats.reduce((sum, s) => sum + s.totalExecutions, 0),
    totalCompleted: stats.reduce((sum, s) => sum + s.completedExecutions, 0),
    avgCompletionRate: stats.reduce((sum, s) => sum + s.completionRate, 0) / stats.length,
    avgComplianceScore: stats.reduce((sum, s) => sum + s.averageComplianceScore, 0) / stats.length
  } : null;

  // Filter stats with executions
  const protocolsWithExecutions = stats.filter(s => s.totalExecutions > 0);

  return (
    <div className="protocol-analytics-dashboard">
      <div className="analytics-header">
        <div>
          {onBack && (
            <button onClick={onBack} className="btn btn-secondary btn-icon" style={{ marginBottom: '12px' }}>
              <ArrowLeft size={16} />
              {t('back')}
            </button>
          )}
          <h2>{t('protocolAnalyticsTitle')}</h2>
          <p className="subtitle">{t('protocolAnalyticsSubtitle')}</p>
        </div>
        <div className="header-actions">
          <button onClick={refresh} className="btn btn-secondary" title={t('refresh')}>
            <RefreshCw size={16} />
            {t('refresh')}
          </button>
        </div>
      </div>

      {/* Date Range Filter */}
      <div className="analytics-filters">
        <div className="filter-group">
          <label htmlFor="start-date">{t('startDate')}</label>
          <input
            type="date"
            id="start-date"
            value={dateRange.start}
            onChange={(e) => setDateRange(prev => ({ ...prev, start: e.target.value }))}
            className="filter-input"
          />
        </div>
        <div className="filter-group">
          <label htmlFor="end-date">{t('endDate')}</label>
          <input
            type="date"
            id="end-date"
            value={dateRange.end}
            onChange={(e) => setDateRange(prev => ({ ...prev, end: e.target.value }))}
            className="filter-input"
          />
        </div>
        {(dateRange.start || dateRange.end) && (
          <div className="filter-group">
            <button
              onClick={() => setDateRange({ start: '', end: '' })}
              className="btn btn-secondary"
            >
              {t('clearFilter')}
            </button>
          </div>
        )}
      </div>

      {/* Overall Statistics */}
      {overallStats && (
        <div className="overall-stats-grid">
          <div className="stat-card">
            <div className="stat-icon total">
              <TrendingUp size={24} />
            </div>
            <div className="stat-content">
              <div className="stat-value">{overallStats.totalProtocols}</div>
              <div className="stat-label">{t('totalProtocols')}</div>
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-icon executions">
              <CheckCircle size={24} />
            </div>
            <div className="stat-content">
              <div className="stat-value">{overallStats.totalExecutions}</div>
              <div className="stat-label">{t('totalExecutions')}</div>
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-icon completed">
              <CheckCircle size={24} />
            </div>
            <div className="stat-content">
              <div className="stat-value">{overallStats.totalCompleted}</div>
              <div className="stat-label">{t('completed')}</div>
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-icon rate">
              <TrendingUp size={24} />
            </div>
            <div className="stat-content">
              <div className="stat-value">{Math.round(overallStats.avgCompletionRate)}%</div>
              <div className="stat-label">{t('avgCompletionRate')}</div>
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-icon compliance">
              <AlertCircle size={24} />
            </div>
            <div className="stat-content">
              <div className="stat-value">{Math.round(overallStats.avgComplianceScore)}%</div>
              <div className="stat-label">{t('avgComplianceScore')}</div>
            </div>
          </div>
        </div>
      )}

      {/* Loading State */}
      {loading && (
        <div className="loading-state">
          <div className="spinner"></div>
          <p>{t('loadingProtocolAnalytics')}</p>
        </div>
      )}

      {/* Error State */}
      {error && (
        <div className="error-state">
          <p>{t('errorLoadingAnalytics', { message: error.message })}</p>
          <button onClick={refresh} className="btn btn-primary">{t('retry')}</button>
        </div>
      )}

      {/* Analytics Table */}
      {!loading && !error && (
        <>
          {protocolsWithExecutions.length === 0 ? (
            <div className="empty-state">
              <p>{t('noProtocolExecutionData')}</p>
              <p className="empty-hint">
                {dateRange.start || dateRange.end
                  ? t('adjustDateRangeHint')
                  : t('protocolsAppearWhenExecuted')}
              </p>
            </div>
          ) : (
            <div className="analytics-table-container">
              <table className="analytics-table">
                <thead>
                  <tr>
                    <th>{t('protocolName')}</th>
                    <th>{t('protocolCode')}</th>
                    <th>{t('tableVersion')}</th>
                    <th>{t('totalExecutions')}</th>
                    <th>{t('completed')}</th>
                    <th>{t('completionRate')}</th>
                    <th>{t('avgComplianceScore')}</th>
                    <th>{t('avgDuration')}</th>
                    <th>{t('onTimeRate')}</th>
                  </tr>
                </thead>
                <tbody>
                  {protocolsWithExecutions.map((stat) => (
                    <tr key={stat.protocolId} className="analytics-row">
                      <td>
                        <div className="protocol-name-cell">
                          <strong>{stat.protocolName || 'Unknown'}</strong>
                        </div>
                      </td>
                      <td>
                        <span className="protocol-code-badge" style={{ fontFamily: 'monospace' }}>
                          {stat.protocolCode || 'N/A'}
                        </span>
                      </td>
                      <td>
                        <span className="version-badge">
                          {stat.protocolVersion || '1.0'}
                        </span>
                      </td>
                      <td>
                        <span className="execution-count">{stat.totalExecutions}</span>
                      </td>
                      <td>
                        <span className="completed-count">{stat.completedExecutions}</span>
                      </td>
                      <td>
                        <div className="completion-rate-cell">
                          <div
                            className="completion-rate-bar"
                            style={{
                              width: `${stat.completionRate}%`,
                              backgroundColor: getCompletionRateColor(stat.completionRate)
                            }}
                          />
                          <span className="completion-rate-text">
                            {stat.completionRate.toFixed(1)}%
                          </span>
                        </div>
                      </td>
                      <td>
                        <span
                          className="compliance-score-badge"
                          style={{ color: getComplianceScoreColor(stat.averageComplianceScore) }}
                        >
                          {stat.averageComplianceScore.toFixed(1)}%
                        </span>
                      </td>
                      <td>
                        <span className="duration-badge">
                          {formatDuration(stat.averageDuration)}
                        </span>
                      </td>
                      <td>
                        <span className="on-time-rate">
                          {stat.onTimeRate.toFixed(1)}%
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default ProtocolAnalyticsDashboard;

